import { useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { AbsolutePopup, PopPaper, TPopupVertical } from 'core-components/library/popup'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import type { IPendingPbmReplacement } from '../../../../lib'
import type { IAttentionReminderRoute } from '../../../../lib'
import {
  useAttentionPendingPbmReplacements,
  useCanReplaceAttentionPbm,
} from '../../hooks/useAttentionPbmReplacement'
import { useAttentionReminder } from '../../hooks/useAttentionReminder'
import { useAttentionReminderScope } from '../../hooks/useAttentionReminderScope'
import type { IAttentionStatusesReminder } from '../../types'
import {
  AttentionPbmName,
  AttentionPbmPicker,
  AttentionPbmReplaceButton,
} from '../attentionPbmReplacement'
import { AttentionReminderButton } from '../attentionReminderButton'

import styles from './AttentionPbmReminders.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses

/** Faces the stack shows before it folds the rest into `+N`. */
const MAX_AVATARS = 3

/** No status picked: the partner's PBMs get the summary of its open statuses. */
const SUMMARY_ROUTE: IAttentionReminderRoute = { bitValue: null, actionId: 'remindPbm' }

/** `Anna Ward` → `AW`; a PBM the payload names only by their email gets its first letter. */
const getInitials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase()

interface IPbmListProps {
  pbms: IAttentionStatusesReminder[]
  route: IAttentionReminderRoute
  isAllReminded: boolean
  /** The service has not said yet who can be reminded. */
  isChecking: boolean
  onRemindAll: () => void
  getPending: (email: string) => IPendingPbmReplacement | undefined
  onReplace?: (pbm: IAttentionStatusesReminder) => void
}

const PbmList = ({
  pbms,
  route,
  isAllReminded,
  isChecking,
  onRemindAll,
  getPending,
  onReplace,
}: IPbmListProps) => (
  <>
    <div className={styles.heading}>
      <Translation span text={KEYS.units.pbm.many} />
    </div>
    {pbms.map((pbm) => {
      const pending = getPending(pbm.email)

      return (
        <div key={pbm.email} className={styles.row}>
          <AttentionPbmName name={pbm.name} pending={pending} className={styles.name} />
          <span className={styles.rowActions}>
            {onReplace && !pending && (
              <AttentionPbmReplaceButton name={pbm.name} onClick={() => onReplace(pbm)} />
            )}
            <AttentionReminderButton reminder={pbm} route={route} filled />
          </span>
        </div>
      )
    })}
    {pbms.length > 1 && (
      <button
        type="button"
        className={styles.remindAll}
        disabled={isAllReminded || isChecking}
        onClick={onRemindAll}
      >
        <i className="kbase kbase-bell" aria-hidden="true" />
        <Translation span text={KEYS.reminder.remindAll} />
      </button>
    )}
  </>
)

interface IAttentionPbmRemindersProps {
  pbms: IAttentionStatusesReminder[]
  /** The status the partner's PBMs are reminded about; the summary of the partner without one. */
  route?: IAttentionReminderRoute
  /** The partner the row stands for; a manager replaces its PBMs from the same list. */
  partner?: { partnerId: string; partnerName: string }
}

/**
 * The PBMs of a partner row, as a stack of initials. The row cannot be opened inside the tile, so
 * the stack opens the list of those PBMs instead, each with a reminder of their own — and for a
 * manager, a swap that turns the list into the pick of who takes the partner over.
 */
export const AttentionPbmReminders = ({
  pbms,
  partner,
  route = SUMMARY_ROUTE,
}: IAttentionPbmRemindersProps) => {
  const t = useSafetyTranslation()
  const [isOpened, setOpened] = useState(false)
  const scope = useAttentionReminderScope()
  const { remind, stateOf, isChecking } = useAttentionReminder({
    recipients: pbms,
    route,
    scope,
    enabled: isOpened,
  })
  const [replacing, setReplacing] = useState<IAttentionStatusesReminder | null>(null)
  const canReplace = useCanReplaceAttentionPbm() && Boolean(partner)
  const getPending = useAttentionPendingPbmReplacements(
    partner ? [partner.partnerId] : [],
    canReplace && isOpened
  )
  const shown = pbms.slice(0, MAX_AVATARS)
  const hiddenCount = pbms.length - shown.length
  const isAllReminded = pbms.every(({ email }) => stateOf(email) === 'remindedToday')

  return (
    <AbsolutePopup
      rootClassName={styles.root}
      verticalStartPosition={TPopupVertical.Bottom}
      visibilityCallback={(visible) => {
        setOpened(visible)
        if (!visible) {
          setReplacing(null)
        }
        return visible
      }}
      popupContent={() => (
        <PopPaper shadowPosition="auto" className={styles.popup}>
          {partner && replacing ? (
            <AttentionPbmPicker
              pbm={replacing}
              partners={[partner]}
              assignedEmails={pbms.map(({ email }) => email)}
              onBack={() => setReplacing(null)}
              onDone={() => setReplacing(null)}
            />
          ) : (
            <PbmList
              pbms={pbms}
              route={route}
              isAllReminded={isAllReminded}
              isChecking={isChecking}
              onRemindAll={() => void remind()}
              getPending={(email) => (partner ? getPending(partner.partnerId, email) : undefined)}
              onReplace={canReplace ? setReplacing : undefined}
            />
          )}
        </PopPaper>
      )}
    >
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="dialog"
        aria-expanded={isOpened}
        aria-label={`${t(KEYS.units.pbm.many)}: ${pbms.map(({ name }) => name).join(', ')}`}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {shown.map(({ email, name }) => (
          <span key={email} className={styles.avatar} aria-hidden="true">
            {getInitials(name)}
          </span>
        ))}
        {hiddenCount > 0 && (
          <span className={styles.avatar} aria-hidden="true">
            +{hiddenCount}
          </span>
        )}
      </button>
    </AbsolutePopup>
  )
}

AttentionPbmReminders.displayName = 'AttentionPbmReminders'
